import { CSSProperties, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import PlaylistingCampaignDetails from "pages/MarketingPage/Playlisting/CampaignDetails";
import VideoCampaignDetails from "pages/MarketingPage/VideoAds/CampaignDetails";
import PreSaveDetails from "../MarketingPage/PreSave/Details/PreSaveDetails";
import Loader from "elements/Loader";
import PageSymphony from "elements/PageSymphony";
import useScreen from "Hooks/useScreen";
import { clsx, styx } from "modules/Utils";
import CampaignDetailsV2 from "pages/CampaignDetailsV2";
import useCampaignDetails from "Hooks/useCampaignDetails";
import { AdPlatform, PLAYBOOK_TYPE_INFO, PlaybookType } from "modules/Const";
import TagSymphony from "elements/TagSymphony";
import ButtonSymphony from "elements/ButtonSymphony";
import FbTokenExpired from "pages/MarketingPage/Components/FbTokenExpired";
import { removeCampaignError_api } from "api/campaignApi";
import useFacebookConnections from "Hooks/useFacebookConnections";
import { toastSuccess, toastError } from "modules/Toast";
import useBrand from "Hooks/useBrand";
import { FacebookCampaignSubmissionType } from "modules/Campaign";
import { pageView, track } from "analytics";

/** Label shown when the campaign data is loading */
const LOADING_LABEL = "Loading Campaign Data...";

interface MarketingCampaignDetailsProps {
    className: string;
    style?: CSSProperties;
    isPublic?: boolean;
}

// NOTE: component()
const MarketingCampaignDetails = ({
    className,
    style,
    isPublic = false
}: MarketingCampaignDetailsProps) => {
    const history = useHistory()
    const params = useParams<{ campaignId?: string }>();
    const { cssCalc } = useScreen();
    const { loggedInFacebookUser, initializeFB } = useFacebookConnections();
    const { getBrand } = useBrand()

    const [needsFacebookRelogin, setNeedsFacebookRelogin] = useState<boolean>(false);

    /** Campaign ID from URL parameters (converted from string to number) */
    const { campaignId: campaignIdString } = params; // NOTE: it's a string because it's coming from the URL
    const campaignId = Number(campaignIdString)

    const {
        isLoadingCampaign,
        getCampaign,
        campaign,
    } = useCampaignDetails()

    // Initialize Facebook SDK
    useEffect(() => {
        initializeFB();
    }, []);

    useEffect(() => {
        getCampaign(campaignId)
    }, [campaignId]);

    // Track campaign details view when campaign is loaded
    useEffect(() => {
        if (campaign && !isLoadingCampaign) {
            const playbook = campaign.campaign_metadata?.playbook || campaign.campaign_metadata?.campaign_type;
            const adPlatform = PLAYBOOK_TYPE_INFO[playbook as PlaybookType]?.adPlatform;
            
            // Track page view
            pageView('ADBOX Campaign Details', {
                campaignId: campaignId,
                playbook: playbook,
                adPlatform: adPlatform,
                isPublic: isPublic,
                version: 'v1'
            });

            // Track detailed event
            track('ADBOX Campaign Details Viewed', {
                version: 'v1',
                campaignId: campaignId,
                playbook: playbook,
                adPlatform: adPlatform,
                campaignStatus: campaign.status,
                isPublic: isPublic,
                timestamp: new Date().toISOString()
            });
        }
    }, [campaign, isLoadingCampaign, campaignId, isPublic]);

    const handleFacebookLoginSuccess = async () => {
        try {
            setNeedsFacebookRelogin(false);
            // After successful Facebook login, remove any FB errors and refresh campaign data
            if (campaign) {
                await removeCampaignError_api(campaign);
            }

            // reload the brand and reinitialize FB to get the latest state of the access token
            await getBrand();
            await initializeFB();
            await getCampaign(campaignId);
            toastSuccess('Successfully reconnected to Facebook');
        } catch (error) {
            console.error('Error after Facebook login:', error);
            toastError('There was an issue loading your campaign data');
        }
    }

    /**
     * Determines which campaign details component to render based on campaign type and playbook
     * @returns The appropriate campaign details component
     */
    const renderCampaignDetails = () => {
        const {
            campaign_metadata,
        } = campaign || {};


        const campaignType = campaign_metadata?.campaign_type;
        const playbook: PlaybookType = (campaign_metadata?.playbook || campaignType) as PlaybookType;
        const adPlatform = PLAYBOOK_TYPE_INFO[playbook]?.adPlatform;


        if (adPlatform === AdPlatform.META) {
            const v2_campaign_input = campaign_metadata?.v2_campaign_input as FacebookCampaignSubmissionType;

            const usesExpressSetup = v2_campaign_input?.usesExpressSetup || !v2_campaign_input?.fbAdAccount || !v2_campaign_input?.fbPage;
            // Check for Facebook auth issues: either explicit error OR Meta campaign with user not logged in
            console.log('usesExpressSetup', usesExpressSetup);
            console.log('loggedInFacebookUser', loggedInFacebookUser);
            console.log('campaign?.campaign_metadata.error?.id', campaign?.campaign_metadata.error?.id);
            const hasFacebookAuthIssue = !usesExpressSetup && (
                campaign?.campaign_metadata.error?.id === "FB_ACCESS_TOKEN_INVALID" ||
                (!loggedInFacebookUser)
            )

            if (hasFacebookAuthIssue && campaign) {
                return (
                    <FbTokenExpired
                        onSuccess={handleFacebookLoginSuccess}
                        campaignId={campaign.id}
                        campaignType={campaign.campaign_metadata.playbook}
                    />
                )
            }
        }

        switch (adPlatform) {
            case AdPlatform.META:
            case AdPlatform.TIKTOK:
                return (
                    <CampaignDetailsV2
                        className="campaignDetailsV2_InstagramOrTikTok"
                        isPublic={isPublic}
                    />
                )
            case AdPlatform.YOUTUBE:
                return (
                    <VideoCampaignDetails
                        campaignDetails={campaign}
                        handleGoBack={() => history.push('/marketing')}
                    />
                )
            case AdPlatform.PLAYLISTS:
                return (
                    <PlaylistingCampaignDetails />
                )
            default:
                if (campaignType === 'pre_save') {
                    return <PreSaveDetails className="preSaveDetailsFromMarketingCampaignDetails" />
                } else {
                    return (
                        <div
                            className="unsupportedCampaignDetails"
                            style={sty.unsupportedCampaignDetails}
                        >
                            <div>
                                Unsupported campaign / playbook type
                            </div>
                            <TagSymphony className="unsupportedCampaignTag" title={playbook} />
                            <ButtonSymphony
                                className="backToMarketingButton"
                                onClick={() => history.push('/marketing')}
                            >
                                Go Back to Marketing
                            </ButtonSymphony>
                        </div>
                    )
                }
        }
    };

    // NOTE: render()
    return (
        <PageSymphony
            className={clsx("marketingCampaignDetailsComponent", className)}
            style={style}
            isContent
        >
            {isLoadingCampaign ?
                <div
                    className="marketingCampaignDetailsLoading"
                    style={styx(sty.marketingCampaignDetailsLoading, { height: cssCalc() })}
                >
                    <Loader className="marketingCampaignDetailsLoading" size={44} />
                    <div className="mt-1 text-center">
                        {LOADING_LABEL}
                    </div>
                </div>
                :
                renderCampaignDetails()
            }
        </PageSymphony>
    );
};

const sty: Record<string, CSSProperties> = {
    marketingCampaignDetailsLoading: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
    },
    unsupportedCampaignDetails: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
    }
}

export default MarketingCampaignDetails;
